import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { EVENTBRITE_URL } from "@/lib/data";
import { track } from "@/lib/track";

const LINKS = [
  { href: "#program", label: "Program" },
  { href: "#curriculum", label: "Curriculum" },
  { href: "#instructor", label: "Instructor" },
  { href: "#pricing", label: "Pricing" },
  { href: "#faq", label: "FAQ" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="site-nav"
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[color:var(--bone)]/85 backdrop-blur-md border-b border-[color:var(--line)]"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1360px] px-5 md:px-8 h-[64px] flex items-center justify-between">
        {/* Packt logo */}
        <a
          href="#top"
          data-testid="nav-logo"
          className="flex items-center gap-2 group"
        >
          <span className="font-serif text-[22px] leading-none tracking-tight text-[color:var(--ink)] italic font-medium">
            Packt
          </span>
          <span className="hidden sm:inline-block h-4 w-px bg-[color:var(--line-strong)]" />
          <span className="hidden sm:inline-block font-mono text-[10px] uppercase tracking-[0.18em] text-[color:var(--ink-3)]">
            Certification
          </span>
        </a>

        {/* Desktop links */}
        <nav className="hidden lg:flex items-center gap-1">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="px-3 py-2 text-[13px] font-medium text-[color:var(--ink-2)] hover:text-[color:var(--copper)] transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="#pricing"
            data-testid="nav-cta-reserve"
            onClick={() => track("nav_cta_click", { location: "nav" })}
            className="btn-ink !py-2.5 !px-4 !text-[13px] !hidden md:!inline-flex"
          >
            Reserve Your Place
            <span className="inline-block">→</span>
          </a>
          <button
            aria-label="Open menu"
            data-testid="nav-menu-toggle"
            onClick={() => setOpen((s) => !s)}
            className="lg:hidden w-10 h-10 grid place-items-center border border-[color:var(--line-strong)] rounded-full"
          >
            {open ? <X size={16} /> : <Menu size={16} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            key="mobile-menu"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden absolute inset-x-0 top-[64px] bg-[color:var(--bone)] border-b border-[color:var(--line)]"
          >
            <div className="px-5 py-6 flex flex-col gap-1">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                  className="py-3 border-b border-[color:var(--line)] font-serif text-2xl text-[color:var(--ink)]"
                >
                  {l.label}
                </a>
              ))}
              <a
                href={EVENTBRITE_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-nav-eventbrite"
                className="mt-4 font-mono text-[11px] uppercase tracking-[0.2em] text-[color:var(--ink-3)]"
              >
                View on Eventbrite →
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
